import type { ModalContext } from './modal-context.js'
import { ContextProvider } from '@lit/context'
import * as closeIcon from '@nordhealth/icons/lib/assets/interface-close-small.js'
import { html, LitElement } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'

import { SlotController } from '../common/controllers/SlotController.js'
import { observe } from '../common/decorators/observe.js'
import { NordEvent } from '../common/events.js'
import componentStyle from '../common/styles/Component.css'

import Icon from '../icon/Icon.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import { modalContext } from './modal-context.js'
import style from './Modal.css'
import { ModalController } from './ModalController.js'

import '../footer/Footer.js'

Icon.registerIcon(closeIcon)

/**
 * Event dispatched when a modal is about to be dismissed.
 * The `trigger` property contains the original event that caused the dismiss.
 */
export class ModalCancelEvent extends NordEvent {
  declare trigger: Event

  constructor(trigger: Event) {
    super('cancel', { cancelable: true })
    Object.defineProperty(this, 'trigger', {
      value: trigger,
      enumerable: true,
      writable: false,
    })
  }
}

/**
 * Event dispatched when a modal is closed.
 * The `trigger` property contains the original event that caused the close, if available.
 */
export class ModalCloseEvent extends NordEvent {
  declare trigger: Event | undefined

  constructor(trigger?: Event) {
    super('close')
    Object.defineProperty(this, 'trigger', {
      value: trigger,
      enumerable: true,
      writable: false,
    })
  }
}

/**
 * Modal component is used to display content that temporarily blocks interactions
 * with the main view of an application. Modal should be used sparingly and
 * only when necessary.
 *
 * @status ready
 * @category overlay
 * @slot - Default slot
 * @slot header - Slot which holds the header of the modal, positioned next to the close button.
 * @slot feature - Slot for full bleed content like an image.
 * @slot footer - Slot which is typically used to hold call to action buttons, but can also be used to build custom footers.
 * @fires {ModalCancelEvent} cancel - Dispatched before the modal has closed when a user attempts to dismiss a modal. The event includes a trigger property containing the original event that caused the dismiss. Call preventDefault() on the event to prevent the modal closing.
 * @fires {ModalCloseEvent} close - Dispatched when a modal is closed for any reason. The event includes an optional trigger property containing the original event that caused the close, if the modal was closed by a user action.
 *
 * @cssprop [--n-modal-padding-inline=var(--n-space-m)] - Controls the padding on the sides of the modal, using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-modal-padding-block=var(--n-space-m)] - Controls the padding above and below the header of the modal, using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-modal-max-inline-size=620px] - Controls the width of the modal.
 *
 * @localization closeLabel - Accessible label for the close button.
 */
@customElement('nord-modal')
export default class Modal extends LitElement {
  static styles = [componentStyle, style]

  @query('.n-modal', true) private modal!: HTMLDivElement
  @query('.n-modal-backdrop', true) private backdrop!: HTMLDivElement

  private lastTrigger?: Event

  private defaultSlot = new SlotController(this)
  private headerSlot = new SlotController(this, 'header')
  private featureSlot = new SlotController(this, 'feature')
  private footerSlot = new SlotController(this, 'footer')

  private localize = new LocalizeController<'nord-modal'>(this)
  private closeContext: ModalContext = {
    requestClose: trigger => this.requestClose(trigger),
  }

  private closeProvider = new ContextProvider(this, {
    context: modalContext,
    initialValue: this.closeContext,
  })

  private modalController = new ModalController(this, {
    isOpen: () => this.open,
    onDismiss: (e: Event) => this.handleDismiss(e),
    isLightDismissEnabled: () => !this.persistent,
    dialog: () => this.modal,
    backdrop: () => this.backdrop,
    close: (returnValue, trigger) => this.close(returnValue, trigger),
  })

  /**
   * Controls whether the modal is open or not.
   */
  @property({ type: Boolean, reflect: true }) open = false

  /**
   * Controls the max-width of the modal when open.
   */
  @property({ reflect: true }) size: 's' | 'm' | 'l' | 'xl' = 'm'

  /**
   * The reason why the modal was closed. This typically indicates
   * which button the user pressed to close the modal, though any value
   * can be supplied if the modal is programmatically closed.
   */
  @property({ attribute: false }) returnValue: string = ''

  /**
   * By default if a modal is too big for the browser window,
   * the entire modal will scroll. This setting changes that behavior
   * so that the body of the modal scrolls instead, with the modal
   * itself remaining fixed.
   */
  @property({ type: Boolean, reflect: true }) scrollable = false

  /**
   * When true, the modal will not close when clicking the backdrop or pressing Escape,
   * and the close button will be hidden. Only programmatic close or custom action buttons
   * can dismiss the modal.
   */
  @property({ type: Boolean, reflect: true }) persistent = false

  connectedCallback(): void {
    super.connectedCallback()

    this.setAttribute('role', 'dialog')
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()

    // Clear trigger reference when modal is removed from DOM to prevent memory leaks
    this.lastTrigger = undefined
  }

  /**
   * Show the modal, automatically moving focus to the modal or a child
   * element with an `autofocus` attribute.
   */
  showModal() {
    this.open = true
  }

  /**
   * Programmatically close the modal.
   * @param returnValue An optional value to indicate why the modal was closed.
   * @param trigger An optional event that triggered the close.
   */
  close(returnValue?: string, trigger?: Event) {
    this.open = false

    if (returnValue != null) {
      this.returnValue = returnValue
    }

    const closeTrigger = trigger ?? this.lastTrigger
    this.dispatchEvent(new ModalCloseEvent(closeTrigger))
    this.lastTrigger = undefined
  }

  /**
   * Programmatically focus the modal.
   * @param options An object which controls aspects of the focusing process.
   */
  focus(options?: FocusOptions) {
    this.modal.focus({ preventScroll: true, ...options })
  }

  /**
   * Request dismissal of the modal through the same cancelable flow as the
   * built-in close button: the `cancel` event fires first and, if not
   * prevented, the modal closes. Used by composed `<nord-modal-close>` parts so
   * a consumer-placed close control behaves identically to the built-in one.
   * @param trigger An optional event that triggered the request.
   */
  requestClose(trigger?: Event) {
    this.handleDismiss(trigger ?? new Event('close'))
  }

  render() {
    return html`
      <div class="n-modal-backdrop">
        <div
          class=${classMap({
            'n-modal': true,
            'n-overflow-hidden': this.featureSlot.hasContent && this.defaultSlot.isEmpty && this.footerSlot.isEmpty,
          })}
          tabindex="0"
        >
          <!-- eslint-disable-next-line @nordhealth/no-unknown-legacy-classes -->
          <div class="n-modal-header n-rounded-top" ?hidden=${this.headerSlot.isEmpty}>
            <slot class="n-padded" name=${this.headerSlot.slotName}></slot>
            <button class="n-close" @click=${(e: Event) => this.handleDismiss(e)} ?hidden=${this.persistent}>
              <nord-icon name="interface-close-small" size="s" label=${this.localize.term('closeLabel')}></nord-icon>
            </button>
          </div>

          <div class="n-modal-body">
            <slot
              name=${this.featureSlot.slotName}
              class=${this.headerSlot.isEmpty ? 'n-rounded-top' : ''}
              ?hidden=${this.featureSlot.isEmpty}
            ></slot>
            <slot class=${this.defaultSlot.isEmpty ? '' : 'n-body-padded'}></slot>
          </div>

          <nord-footer ?hidden=${this.footerSlot.isEmpty}>
            <slot name=${this.footerSlot.slotName}></slot>
          </nord-footer>
        </div>
      </div>
    `
  }

  @observe('open', 'updated')
  protected handleOpenUpdated(prev: boolean) {
    if (this.open) {
      this.modalController.block()
    }
    else if (prev === true) {
      this.modalController.unblock()
    }
  }

  private handleDismiss(trigger: Event) {
    // allow cancelling of close
    const allowed = this.dispatchEvent(new ModalCancelEvent(trigger))

    if (allowed) {
      this.lastTrigger = trigger
      this.close()
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-modal': Modal
  }
}
